import React, { useEffect, useState } from 'react';
import { LuRepeat2 } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { getTargetedReconciliation } from '@/store/selectors/targetedReconciliation.selector';
import {
  ALL_OPERATOR,
  ROW_PER_PAGE_OPTION,
  STRINGS,
  pagination as PAGINATION_INITIAL_STATE,
  ALL_PROVIDER,
} from '@/constant/global/global.constant';
import { ITargetedReconciliationRecord } from '@/services/targetedReconciliation/types';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { isEmpty } from 'lodash';
import { DataTable, type ColumnDef } from '@/components/organism/Table';
import { fetchTargetedReconciliationRecords } from '@/store/slices/targetedReconciliation.slice';
import { isSameRequestParams } from '@/utils/requestParamsChecker';
import useDeepEffect from '@/hooks/useDeepEffect';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import Autocomplete from '@/components/elements/AutoComplete';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import {
  TARGETED_RECONCILIATION_PROPS,
  TARGETED_RECONCILIATION_STATUS_FILTERS,
} from './constants';
import { buildColumns } from './targetedReconciliation.columns';

const TargetedReconciliation: React.FC = () => {
  const toast = useToast();
  const [isCopy, setIsCopy] = useState(false);
  const [filter, setFilter] = useState({ startDate: '', endDate: '' });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [sorting, setSorting] = useState({
    orderBy: 'createdOn',
    order: 'desc',
  });
  const [selection, setSelection] = useState({
    provider: ALL_PROVIDER,
    operator: ALL_OPERATOR,
  });
  const dispatch = useDispatch();
  const targetedReconciliation = useSelector(getTargetedReconciliation);
  const data = targetedReconciliation?.data || [];
  const paginationResponse = targetedReconciliation?.pagination || {};
  const loading = useSelector(
    (state: any) => state.targetedReconciliation.loading,
  );
  const error = useSelector((state: any) => state.targetedReconciliation.error);
  const errorMessage = useSelector(
    (state: any) => state.targetedReconciliation.message,
  );
  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / (pagination.limit || 10),
  );
  useEffect(() => {
    if (
      error &&
      errorMessage &&
      toast &&
      typeof toast.pushError === 'function'
    ) {
      toast.pushError(errorMessage);
      dispatch({
        type: 'targetedReconciliation/clearTargetedReconciliationError',
      });
    }
  }, [error, errorMessage, toast, dispatch]);
  const [isStateMutated, setIsStateMutated] = useState(false);
  const [sourceTransactionId, setSourceTransactionId] = useState('');
  const [transactionStatus, setTransactionStatus] = useState(
    TARGETED_RECONCILIATION_STATUS_FILTERS[0],
  );

  const fetchAllTargetedReconciliationData = async (param: any) => {
    dispatch(fetchTargetedReconciliationRecords({ ...param }));
  };
  useDeepEffect(() => {
    setIsStateMutated(true);
  }, [dispatch, pagination, filter, selection, sorting]);

  const requestIdQuery = useDebounce(
    sourceTransactionId?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  useEffect(() => {
    const targetedReconciliationPayload = {
      ...pagination,
      startDtm: filter?.startDate,
      endDtm: filter?.endDate,
      operatorId:
        selection?.operator?.id !== ALL_OPERATOR.id
          ? selection?.operator?.sourceId
          : undefined,
      providerId:
        selection?.provider?.id !== ALL_PROVIDER.id
          ? selection?.provider?.id
          : undefined,
      orderby: sorting.orderBy,
      order: sorting.order,
      ...(requestIdQuery ? { vdiTransactionId: requestIdQuery } : {}),
      ...(transactionStatus?.queryParam !==
      TARGETED_RECONCILIATION_STATUS_FILTERS[0]?.queryParam
        ? { status: transactionStatus?.queryParam }
        : {}),
    };
    if (isStateMutated) {
      if (isSameRequestParams({ ...targetedReconciliationPayload })) {
        return;
      }
      fetchAllTargetedReconciliationData(targetedReconciliationPayload);
    }
  }, [
    dispatch,
    pagination,
    filter,
    selection,
    sorting,
    requestIdQuery,
    transactionStatus?.queryParam,
  ]);

  const handleDateFilter = (val: any) => {
    setFilter({ startDate: val.startDate, endDate: val.endDate });
  };

  const handleSelectionChange = ({ provider, operator }: any) => {
    setSelection({ provider, operator });
  };

  const handlePageSizeChange = (value: number) => {
    if (totalPages < pagination?.page) {
      setPagination({ page: totalPages, limit: value });
    } else {
      setPagination({ ...pagination, limit: value });
    }
  };

  const handlePreviousClick = (page: number) => {
    if (page > 1 && page <= totalPages) {
      setPagination({ ...pagination, page: page - 1 });
    }
  };

  const handleNextClick = (page: number) => {
    if (page >= 1 && page < totalPages) {
      setPagination({ ...pagination, page: page + 1 });
    }
  };

  const onCopyClick = () => {
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 500);
  };
  const searchBySourceTransactionIdFn = (value: any) => {
    setSourceTransactionId(value);
  };
  const handleStatusChange = (value: any) => {
    setTransactionStatus(value);
  };

  return (
    <FullPageCard
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.TARGETED_RECONCILIATION}
          subtitle={STRINGS.TARGETED_RECONCILIATION_SUB}
          icon={getNavItemIcon(NAV_ITEMS.TARGETED_RECONCILIATION)}
          count={paginationResponse?.totalRecords}
          actionsSlot={
            <GlobalSearch
              className="w-[240px]"
              placeholder={
                TARGETED_RECONCILIATION_PROPS.SEARCH
                  .SOURCE_TRANSACTION_ID_PLACEHOLDER
              }
              globalSearch={sourceTransactionId}
              setGlobalSearch={searchBySourceTransactionIdFn}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleSelectionChange}
              showProvider
              showOperator
              showLocation={false}
              showMarket={false}
              showDevice={false}
            />
          }
          filtersRight={
            <Autocomplete
              label={TARGETED_RECONCILIATION_PROPS.STATUS_FILTER.LABEL}
              placeholder={TARGETED_RECONCILIATION_PROPS.STATUS_FILTER.LABEL}
              handleSelectedValue={handleStatusChange}
              selectedValue={transactionStatus}
              handleSearch={setTransactionStatus}
              lists={TARGETED_RECONCILIATION_STATUS_FILTERS}
            />
          }
        />
      }
    >
      <DataTable<ITargetedReconciliationRecord>
        data={data as unknown as ITargetedReconciliationRecord[]}
        loading={loading}
        skeletonRows={15}
        columns={buildColumns(isCopy, onCopyClick)}
        onSort={(orderBy: any, order: any) => setSorting({ orderBy, order })}
        sortState={sorting as any}
        pagination={
          !isEmpty(data)
            ? {
                state: pagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
            : null
        }
        rowKey={(item) =>
          (item as any)?.sourceTransactionId ?? Math.random().toString()
        }
      />
    </FullPageCard>
  );
};

export default TargetedReconciliation;
